import { useRef, useEffect, useState } from "react";
import { Eraser, PenLine, Type } from "lucide-react";

/**
 * Simple in-house e-signature capture. Supports "draw" (canvas) and "type" modes.
 * Reports the signature up via onChange({ type: "draw"|"type", data }).
 */
export const SignaturePad = ({ onChange, defaultName = "", testid = "signature-pad" }) => {
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState(defaultName);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    if (mode !== "draw") return;
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2;
    c.height = rect.height * 2;
    const g = c.getContext("2d");
    g.scale(2, 2);
    g.lineWidth = 2.2;
    g.lineCap = "round";
    g.lineJoin = "round";
    g.strokeStyle = "#111111";
    dirty.current = false;
  }, [mode]);

  useEffect(() => {
    if (mode === "type") onChange?.({ type: "type", data: typed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const g = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    g.beginPath();
    g.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const g = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    g.lineTo(x, y);
    g.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) onChange?.({ type: "draw", data: canvasRef.current.toDataURL("image/png") });
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange?.({ type: "draw", data: "" });
  };

  const tabBtn = (m, Icon, label) => (
    <button
      type="button"
      data-testid={`${testid}-mode-${m}`}
      onClick={() => setMode(m)}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${mode === m ? "bg-[#B87333] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {tabBtn("draw", PenLine, "Draw")}
        {tabBtn("type", Type, "Type")}
        {mode === "draw" && (
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={clear}
            className="ml-auto inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-[#C0392B] transition-colors"
          >
            <Eraser size={13} /> Clear
          </button>
        )}
      </div>

      {mode === "draw" ? (
        <canvas
          ref={canvasRef}
          data-testid={`${testid}-canvas`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="w-full h-32 bg-white rounded-sm cursor-crosshair touch-none"
          style={{ touchAction: "none" }}
        />
      ) : (
        <input
          data-testid={`${testid}-input`}
          value={typed}
          onChange={(e) => { setTyped(e.target.value); onChange?.({ type: "type", data: e.target.value }); }}
          placeholder="Type your full name to sign"
          className="w-full bg-white text-[#111] rounded-sm px-4 py-4 outline-none border-2 border-transparent focus:border-[#B87333]"
          style={{ fontFamily: "'Brush Script MT', cursive", fontSize: "28px" }}
        />
      )}
      <p className="font-body text-[11px] text-[#6f6f68] mt-2">
        By signing you confirm this is your electronic signature under the IT Act, 2000.
      </p>
    </div>
  );
};
